import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import { buscarCredencial, copiarParaClipboard } from '../utils/credenciaisBpoRh';

// Cadastro de clientes do BPO RH — espelha todas as colunas de `bporh_cliente`.
// created_at/updated_at são mantidos pelo banco: aparecem na lista e no modal,
// mas em modo leitura (não fazem parte do payload).
const FORM_VAZIO = {
  id: null,
  cliente: '',
  cnpj: '',
  operador: '',
  api_convenia: '',
  webhook_secret: '',
  // grupo que recebe cópia de TODO e-mail automático deste cliente
  email_grupo: '',
  email_grupo_modo: 'cc',
  // vindas da listagem (`tem_*`): indicam que já existe credencial, sem trazer o valor
  tem_api_convenia: false,
  tem_webhook_secret: false,
};

// Serviço (acesso a sistema) de um cliente — colunas de `bporh_servico`
const SERVICO_VAZIO = {
  id: null,
  categoria: '',
  propriedade: '',
  portal: '',
  login: '',
  senha: '',
  email_reset: '',
  api_token: '',
  tem_senha: false,
  tem_api_token: false,
};

// Mesma lista do submenu do Sidebar (CATEGORIAS_BPORH_PADRAO); as categorias que
// já existem no banco são acrescentadas em tempo de execução.
const CATEGORIAS_PADRAO = [
  'Academia', 'Agente Integrador', 'Assinador Digital', 'Benefícios',
  'Chat do cliente', 'Controle de documentos', 'Email', 'Ferramentas', 'Gestão',
  'Medicina do Trabalho', 'Plano de Saúde e Odonto', 'Ponto', 'Vale Transporte',
];

// Guardado só com dígitos no banco; exibido com máscara
const formatarCnpj = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length !== 14) return v || '-';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const BpoRhClientesCadastro = () => {
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  const [busca, setBusca] = useState('');

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Secrets/tokens visíveis por chave (ex.: 'cv-3', 'wh-3', 'sv-senha-12') e os
  // valores já revelados pelo backend — a listagem só traz `tem_<campo>`
  const [visiveis, setVisiveis] = useState([]);
  const [revelados, setRevelados] = useState({});
  // Campos de credencial que a operadora editou (senão o PUT apagaria o segredo)
  const [camposTocados, setCamposTocados] = useState([]);
  const [servicoTocados, setServicoTocados] = useState([]);

  const [mostrarModal, setMostrarModal] = useState(false);
  const [formulario, setFormulario] = useState(FORM_VAZIO);
  const [mostrarConvenia, setMostrarConvenia] = useState(false);
  const [mostrarSecret, setMostrarSecret] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [clienteExcluir, setClienteExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // Sair da carteira do BPO RH: não apaga nada, só tira o cliente das telas
  const [clienteSituacao, setClienteSituacao] = useState(null);
  const [previaSituacao, setPreviaSituacao] = useState(null);
  const [motivoSaida, setMotivoSaida] = useState('');
  const [salvandoSituacao, setSalvandoSituacao] = useState(false);
  const [verInativos, setVerInativos] = useState(false);

  // Serviços do cliente clicado (modal)
  const [clienteServicos, setClienteServicos] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [carregandoServicos, setCarregandoServicos] = useState(false);

  // Formulário de serviço dentro do modal (null = fechado)
  const [servicoForm, setServicoForm] = useState(null);
  const [salvandoServico, setSalvandoServico] = useState(false);
  const [servicoExcluir, setServicoExcluir] = useState(null);
  const [mostrarSenhaForm, setMostrarSenhaForm] = useState(false);

  const carregarClientes = useCallback(() => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/?todos=1`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setClientes(res.data.clientes || []);
        setCarregando(false);
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.erro ||
          error.response?.data?.mensagem ||
          error.message ||
          'Erro ao carregar os clientes';
        setMensagem(`${errorMsg}`);
        setCarregando(false);
      });
  }, []);

  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  // Busca os serviços só do cliente aberto — não trafega a carteira inteira
  const carregarServicos = useCallback(() => {
    if (!clienteServicos) { setServicos([]); return; }
    setCarregandoServicos(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/`, {
        params: { cliente_id: clienteServicos.id },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => setServicos(res.data.servicos || []))
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar os serviços'}`);
        setServicos([]);
      })
      .finally(() => setCarregandoServicos(false));
  }, [clienteServicos]);

  useEffect(() => { carregarServicos(); }, [carregarServicos]);

  // fechar o modal descarta o formulário aberto
  useEffect(() => {
    if (!clienteServicos) { setServicoForm(null); setServicoExcluir(null); }
  }, [clienteServicos]);

  // O formulário e o aviso de exclusão ficam no topo do corpo do modal, que tem
  // rolagem própria. Sem trazer a rolagem de volta, clicar em Editar numa lista
  // longa abria o formulário fora da área visível — parecia que nada acontecia.
  const corpoServicosRef = useRef(null);
  const rolarParaTopo = () => {
    const el = corpoServicosRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const abrirNovoServico = () => {
    setServicoForm({ ...SERVICO_VAZIO });
    setServicoTocados([]);
    setMostrarSenhaForm(false);
    setServicoExcluir(null);
    rolarParaTopo();
  };

  const abrirEdicaoServico = (s) => {
    // senha/api_token não vêm na listagem: abrem vazios e só são enviados se
    // a operadora digitar algo (servicoTocados)
    setServicoForm({
      id: s.id,
      categoria: s.categoria || '',
      propriedade: s.propriedade || '',
      portal: s.portal || '',
      login: s.login || '',
      senha: '',
      email_reset: s.email_reset || '',
      api_token: '',
      tem_senha: !!s.tem_senha,
      tem_api_token: !!s.tem_api_token,
    });
    setServicoTocados([]);
    setMostrarSenhaForm(false);
    setServicoExcluir(null);
    rolarParaTopo();
  };

  const pedirExclusaoServico = (s) => {
    setServicoExcluir(s);
    setServicoForm(null);
    rolarParaTopo();
  };

  const setServ = (campo) => (e) => {
    setServicoForm((f) => ({ ...f, [campo]: e.target.value }));
    setServicoTocados((prev) => (prev.includes(campo) ? prev : [...prev, campo]));
  };

  // Traz a credencial atual para o campo, para editar em cima do valor existente
  const revelarNoServico = (campo) => {
    if (!servicoForm?.id) return;
    buscarCredencial('servico', servicoForm.id, campo)
      .then((valor) => setServicoForm((f) => ({ ...f, [campo]: valor })))
      .catch((error) =>
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter o valor atual'}`));
  };

  const salvarServico = () => {
    if (!servicoForm || !clienteServicos) return;
    if (!servicoForm.categoria.trim() || !servicoForm.propriedade.trim()) {
      setMensagem('Categoria e Propriedade são obrigatórias');
      return;
    }
    setSalvandoServico(true);
    const accessToken = localStorage.getItem('access');
    const headers = { Authorization: `Bearer ${accessToken}` };
    // cliente_id explícito: sem ele o backend tentaria resolver pelo nome
    const payload = {
      cliente_id: clienteServicos.id,
      categoria: servicoForm.categoria.trim(),
      propriedade: servicoForm.propriedade.trim(),
      portal: (servicoForm.portal || '').trim(),
      login: (servicoForm.login || '').trim(),
      email_reset: (servicoForm.email_reset || '').trim(),
    };
    // Omitir a chave = manter o segredo que já está no banco. Num serviço novo
    // sempre vai; na edição, só se foi digitado.
    if (!servicoForm.id || servicoTocados.includes('senha')) {
      payload.senha = (servicoForm.senha || '').trim();
    }
    if (!servicoForm.id || servicoTocados.includes('api_token')) {
      payload.api_token = (servicoForm.api_token || '').trim();
    }
    const base = `${process.env.REACT_APP_API_BASE}/api/bporh-servicos/`;
    const requisicao = servicoForm.id
      ? axios.put(`${base}${servicoForm.id}/`, payload, { headers })
      : axios.post(base, payload, { headers });

    requisicao
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Serviço salvo'}`);
        setServicoForm(null);
        carregarServicos();
        carregarClientes();   // atualiza a contagem de serviços na tabela
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.response?.data?.erro || 'Erro ao salvar o serviço'}`);
      })
      .finally(() => setSalvandoServico(false));
  };

  const confirmarExclusaoServico = () => {
    if (!servicoExcluir) return;
    setSalvandoServico(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/${servicoExcluir.id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Serviço excluído'}`);
        setServicoExcluir(null);
        carregarServicos();
        carregarClientes();
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Erro ao excluir o serviço'}`);
      })
      .finally(() => setSalvandoServico(false));
  };

  const clientesFiltrados = React.useMemo(() => {
    const b = busca.trim().toLowerCase();
    if (!b) return clientes;
    return clientes.filter(c =>
      [c.cliente, c.operador, c.cnpj].filter(Boolean).join(' ').toLowerCase().includes(b)
    );
  }, [clientes, busca]);

  // Quem saiu da carteira fica escondido até o operador pedir para ver
  const clientesVisiveis = React.useMemo(
    () => (verInativos ? clientesFiltrados : clientesFiltrados.filter(c => c.ativo !== false)),
    [clientesFiltrados, verInativos]
  );
  const ativosTotal = React.useMemo(
    () => clientes.filter(c => c.ativo !== false).length, [clientes]
  );
  const inativosTotal = clientes.length - ativosTotal;

  // Busca o valor no backend na primeira vez que a chave é pedida (com log lá)
  const obterRevelado = (chave, tipo, id, campo) => {
    if (revelados[chave] !== undefined) return Promise.resolve(revelados[chave]);
    return buscarCredencial(tipo, id, campo)
      .then((valor) => {
        setRevelados(prev => ({ ...prev, [chave]: valor }));
        return valor;
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter a credencial'}`);
        return null;
      });
  };

  const alternarVisivel = (chave, tipo, id, campo) => {
    if (visiveis.includes(chave)) {
      setVisiveis(prev => prev.filter(v => v !== chave));
      return;
    }
    obterRevelado(chave, tipo, id, campo).then((valor) => {
      if (valor !== null) setVisiveis(prev => [...prev, chave]);
    });
  };

  const copiarTexto = (texto, rotulo) => {
    copiarParaClipboard(texto)
      .then(() => setMensagem(`${rotulo} copiado`))
      .catch((e) => setMensagem(`${e.message || 'Não foi possível copiar'}`));
  };

  const abrirModalNovo = () => {
    setFormulario(FORM_VAZIO);
    setMostrarConvenia(false);
    setMostrarSecret(false);
    setMostrarModal(true);
  };

  const abrirModalEdicao = (cliente) => {
    // token da Convenia e webhook secret não vêm na listagem: abrem vazios e só
    // são enviados se forem digitados (camposTocados)
    setFormulario({
      id: cliente.id,
      cliente: cliente.cliente || '',
      cnpj: cliente.cnpj || '',
      operador: cliente.operador || '',
      api_convenia: '',
      webhook_secret: '',
      email_grupo: cliente.email_grupo || '',
      email_grupo_modo: cliente.email_grupo_modo || 'cc',
      tem_api_convenia: !!cliente.tem_api_convenia,
      tem_webhook_secret: !!cliente.tem_webhook_secret,
      created_at: cliente.created_at,
      updated_at: cliente.updated_at,
    });
    setCamposTocados([]);
    setMostrarConvenia(false);
    setMostrarSecret(false);
    setMostrarModal(true);
  };

  const fecharModal = () => {
    if (salvando) return;
    setMostrarModal(false);
    setFormulario(FORM_VAZIO);
    setCamposTocados([]);
  };

  const handleFormChange = (campo, valor) => {
    setFormulario(prev => ({ ...prev, [campo]: valor }));
    setCamposTocados(prev => (prev.includes(campo) ? prev : [...prev, campo]));
  };

  // Traz a credencial atual para o campo, para editar em cima do valor existente
  const revelarNoFormulario = (campo) => {
    if (!formulario.id) return;
    buscarCredencial('cliente', formulario.id, campo)
      .then((valor) => setFormulario(prev => ({ ...prev, [campo]: valor })))
      .catch((error) =>
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter o valor atual'}`));
  };

  const salvarCliente = () => {
    if (!formulario.cliente.trim()) {
      setMensagem('Informe o nome do cliente');
      return;
    }

    setSalvando(true);
    const accessToken = localStorage.getItem('access');
    const headers = { Authorization: `Bearer ${accessToken}` };
    // .trim() nos segredos: token com \r\n colado do e-mail já derrubou o sync antes
    const payload = {
      cliente: formulario.cliente.trim(),
      cnpj: (formulario.cnpj || '').replace(/\D/g, ''),
      operador: formulario.operador.trim(),
      email_grupo: (formulario.email_grupo || '').trim(),
      email_grupo_modo: formulario.email_grupo_modo || 'cc',
    };
    // Omitir a chave = manter o segredo no banco. Cliente novo sempre envia.
    if (!formulario.id || camposTocados.includes('api_convenia')) {
      payload.api_convenia = (formulario.api_convenia || '').trim();
    }
    if (!formulario.id || camposTocados.includes('webhook_secret')) {
      payload.webhook_secret = (formulario.webhook_secret || '').trim();
    }

    const requisicao = formulario.id
      ? axios.put(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/${formulario.id}/`, payload, { headers })
      : axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/`, payload, { headers });

    requisicao
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Cliente salvo com sucesso'}`);
        setSalvando(false);
        setMostrarModal(false);
        setFormulario(FORM_VAZIO);
        carregarClientes();
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.mensagem ||
          error.response?.data?.erro ||
          'Erro ao salvar o cliente';
        setMensagem(`${errorMsg}`);
        setSalvando(false);
      });
  };

  // Abre o modal já perguntando ao backend o que ainda está em aberto
  const abrirSituacao = (cliente) => {
    setClienteSituacao(cliente);
    setPreviaSituacao(null);
    setMotivoSaida('');
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/${cliente.id}/situacao/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => setPreviaSituacao(res.data))
      .catch(() => setPreviaSituacao({ erro: true }));
  };

  const confirmarSituacao = () => {
    if (!clienteSituacao) return;
    const saindo = clienteSituacao.ativo !== false;
    if (saindo && !motivoSaida.trim()) {
      setMensagem('Informe o motivo da saída');
      return;
    }
    setSalvandoSituacao(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(
        `${process.env.REACT_APP_API_BASE}/api/bporh-clientes/${clienteSituacao.id}/situacao/`,
        { ativo: !saindo, motivo: motivoSaida.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      .then((res) => {
        setMensagem(res.data.mensagem || 'Situação atualizada');
        setSalvandoSituacao(false);
        setClienteSituacao(null);
        carregarClientes();
      })
      .catch((error) => {
        setMensagem(
          error.response?.data?.mensagem ||
          error.response?.data?.erro ||
          'Erro ao mudar a situação do cliente'
        );
        setSalvandoSituacao(false);
      });
  };

  const confirmarExclusao = () => {
    if (!clienteExcluir) return;
    setExcluindo(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/${clienteExcluir.id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Cliente excluído com sucesso'}`);
        setExcluindo(false);
        setClienteExcluir(null);
        carregarClientes();
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.mensagem ||
          error.response?.data?.erro ||
          'Erro ao excluir o cliente';
        setMensagem(`${errorMsg}`);
        setExcluindo(false);
      });
  };

  // `existe` vem do `tem_<campo>` da listagem; o valor só é buscado quando a
  // operadora clica em mostrar/copiar (nunca vem no GET da lista)
  const mascarar = (existe, chave, tipo, id, campo) => {
    if (!existe) return <span className="text-muted">-</span>;
    return (
      <span className="d-inline-flex align-items-center gap-1">
        <code style={{ wordBreak: 'break-all', fontSize: '.78rem' }}>
          {visiveis.includes(chave) ? revelados[chave] : '••••••••'}
        </code>
        <button className="btn btn-sm btn-light p-1"
          onClick={() => alternarVisivel(chave, tipo, id, campo)} title="Mostrar/ocultar">
          <i className={`fas ${visiveis.includes(chave) ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
        </button>
        <button className="btn btn-sm btn-light p-1"
          onClick={() => obterRevelado(chave, tipo, id, campo)
            .then((v) => { if (v !== null) copiarTexto(v, 'Valor'); })}
          title="Copiar">
          <i className="fas fa-copy text-muted"></i>
        </button>
      </span>
    );
  };

  // Categorias do datalist: as padrão + as que já existem nos serviços do cliente
  const categoriasDisponiveis = React.useMemo(() => {
    const doCliente = servicos.map(s => s.categoria).filter(Boolean);
    return [...new Set([...CATEGORIAS_PADRAO, ...doCliente])].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [servicos]);

  // Serviços agrupados por categoria dentro do modal
  const servicosPorCategoria = React.useMemo(() => {
    const grupos = {};
    servicos.forEach(s => {
      const cat = s.categoria || 'Sem categoria';
      (grupos[cat] = grupos[cat] || []).push(s);
    });
    return Object.entries(grupos).sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [servicos]);

  return (
    <div className="bporh-clientes-cadastro-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-building header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Clientes</h2>
          </div>
          <button className="btn btn-primary" onClick={abrirModalNovo}>
            <i className="fas fa-plus me-2"></i>
            Novo Cliente
          </button>
        </div>

        {/* Busca */}
        <div className="row mb-3 g-2">
          <div className="col-md-4">
            <label className="form-label fw-bold mb-0">Busca</label>
            <input
              type="text"
              className="form-control form-control-sm mt-1"
              placeholder="Buscar por cliente, CNPJ ou operador..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          <span className="badge bg-secondary">
            {clientesVisiveis.length} de {ativosTotal} clientes na carteira
          </span>
          {inativosTotal > 0 && (
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="verInativos"
                checked={verInativos}
                onChange={(e) => setVerInativos(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="verInativos">
                mostrar {inativosTotal} fora da carteira
              </label>
            </div>
          )}
          <span className="badge bg-light text-dark border">
            <i className="fas fa-hand-pointer me-1"></i>
            clique em Serviços para ver os acessos do cliente
          </span>
        </div>

        {/* Tabela */}
        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando clientes...</p>
          </div>
        ) : clientesVisiveis.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Nenhum cliente encontrado.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead>
                <tr>
                  <th><i className="fas fa-hashtag me-2"></i>ID</th>
                  <th><i className="fas fa-building me-2"></i>Cliente</th>
                  <th><i className="fas fa-id-card me-2"></i>CNPJ</th>
                  <th><i className="fas fa-user-tie me-2"></i>Operador</th>
                  <th><i className="fas fa-circle-check me-2"></i>Situação</th>
                  <th><i className="fas fa-layer-group me-2"></i>Serviços</th>
                  <th><i className="fas fa-plug me-2"></i>API Convenia</th>
                  <th><i className="fas fa-key me-2"></i>Webhook Secret</th>
                  <th><i className="fas fa-calendar-plus me-2"></i>Criado em</th>
                  <th><i className="fas fa-clock-rotate-left me-2"></i>Atualizado em</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientesVisiveis.map((cliente) => (
                  <tr key={cliente.id} className={cliente.ativo === false ? 'table-light text-muted' : undefined}>
                    <td className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {cliente.id}
                    </td>
                    <td className="fw-bold">{cliente.cliente}</td>
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {cliente.cnpj
                        ? formatarCnpj(cliente.cnpj)
                        : <span className="badge bg-light text-danger border"
                            title="Sem CNPJ o formulário público de admissão não encontra a empresa">
                            faltando
                          </span>}
                    </td>
                    <td>{cliente.operador || <span className="text-muted">-</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {cliente.ativo === false ? (
                        <span
                          className="badge bg-secondary"
                          title={`Fora da carteira${cliente.inativo_em ? ` desde ${cliente.inativo_em}` : ''}`
                            + (cliente.inativo_motivo ? ` — ${cliente.inativo_motivo}` : '')}
                        >
                          <i className="fas fa-circle-minus me-1"></i>fora da carteira
                        </span>
                      ) : (
                        <span className="badge bg-success-subtle text-success border border-success-subtle">
                          <i className="fas fa-circle-check me-1"></i>na carteira
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setClienteServicos(cliente)}
                        title="Ver os serviços deste cliente"
                      >
                        <i className="fas fa-layer-group me-1"></i>
                        {cliente.total_servicos || 0}
                      </button>
                    </td>
                    <td>{mascarar(cliente.tem_api_convenia, `cv-${cliente.id}`, 'cliente', cliente.id, 'api_convenia')}</td>
                    <td>{mascarar(cliente.tem_webhook_secret, `wh-${cliente.id}`, 'cliente', cliente.id, 'webhook_secret')}</td>
                    <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                      {cliente.created_at || '-'}
                    </td>
                    <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                      {cliente.updated_at || '-'}
                    </td>
                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => abrirModalEdicao(cliente)}
                        title="Editar"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className={`btn btn-sm me-1 ${cliente.ativo === false
                          ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                        onClick={() => abrirSituacao(cliente)}
                        title={cliente.ativo === false
                          ? 'Trazer de volta para a carteira do BPO RH'
                          : 'Tirar da carteira do BPO RH (não apaga nada)'}
                      >
                        <i className={`fas ${cliente.ativo === false
                          ? 'fa-rotate-left' : 'fa-right-from-bracket'}`}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setClienteExcluir(cliente)}
                        title="Excluir"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL DE CADASTRO / EDIÇÃO */}
      {mostrarModal && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '900px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className={`fas ${formulario.id ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                    {formulario.id ? 'Editar Cliente' : 'Novo Cliente'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    Dados do cliente, operador responsável e credenciais de integração
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={fecharModal} disabled={salvando}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-building me-2 text-primary"></i>
                      Cliente <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulario.cliente}
                      onChange={(e) => handleFormChange('cliente', e.target.value)}
                      placeholder="Nome da empresa"
                      disabled={salvando}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-id-card me-2 text-primary"></i>
                      CNPJ
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulario.cnpj}
                      onChange={(e) => handleFormChange('cnpj', e.target.value)}
                      placeholder="00.000.000/0000-00"
                      disabled={salvando}
                      inputMode="numeric"
                    />
                    <small className="text-muted">
                      É como o formulário público de admissão identifica a empresa e carrega
                      os cargos, jornadas e departamentos dela.
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-tie me-2 text-primary"></i>
                      Operador
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulario.operador}
                      onChange={(e) => handleFormChange('operador', e.target.value)}
                      placeholder="Quem atende o cliente"
                      disabled={salvando}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">
                      <i className="fas fa-users me-2 text-primary"></i>
                      Grupo de e-mail do cliente
                    </label>
                    <div className="d-flex gap-2 flex-wrap">
                      <input
                        type="text"
                        className="form-control"
                        style={{ minWidth: 280, flex: 1 }}
                        value={formulario.email_grupo}
                        onChange={(e) => handleFormChange('email_grupo', e.target.value)}
                        placeholder="rh@cliente.com.br; financeiro@cliente.com.br"
                        disabled={salvando}
                      />
                      <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={formulario.email_grupo_modo}
                        onChange={(e) => handleFormChange('email_grupo_modo', e.target.value)}
                        disabled={salvando}
                        title="Cc: o grupo aparece no e-mail e pode responder a todos. Cco: cópia oculta."
                      >
                        <option value="cc">Cc (visível)</option>
                        <option value="bcc">Cco (oculta)</option>
                      </select>
                    </div>
                    <div className="form-text">
                      Recebe cópia de <b>todo e-mail automático deste cliente</b> —
                      aviso de ponto, férias, prorrogação, fechamento, boletos de VA
                      e VT, contratos e avisos de admissão/rescisão. Separe por
                      ponto e vírgula. Em branco, nada é copiado.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-plug me-2 text-primary"></i>
                      API Convenia
                    </label>
                    <div className="input-group">
                      <input
                        type={mostrarConvenia ? 'text' : 'password'}
                        className="form-control"
                        value={formulario.api_convenia}
                        onChange={(e) => handleFormChange('api_convenia', e.target.value)}
                        placeholder={formulario.tem_api_convenia
                          ? 'Deixe em branco para manter o token atual'
                          : 'Token da API Convenia'}
                        disabled={salvando}
                        autoComplete="new-password"
                      />
                      {formulario.tem_api_convenia && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => revelarNoFormulario('api_convenia')}
                          title="Carregar o token atual para editar"
                          tabIndex={-1}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setMostrarConvenia(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fas ${mostrarConvenia ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <small className="text-muted">Usado pelo sync e pela criação de admissões.</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-key me-2 text-primary"></i>
                      Webhook Secret
                    </label>
                    <div className="input-group">
                      <input
                        type={mostrarSecret ? 'text' : 'password'}
                        className="form-control"
                        value={formulario.webhook_secret}
                        onChange={(e) => handleFormChange('webhook_secret', e.target.value)}
                        placeholder={formulario.tem_webhook_secret
                          ? 'Deixe em branco para manter o segredo atual'
                          : 'Segredo HMAC do webhook'}
                        disabled={salvando}
                        autoComplete="new-password"
                      />
                      {formulario.tem_webhook_secret && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => revelarNoFormulario('webhook_secret')}
                          title="Carregar o segredo atual para editar"
                          tabIndex={-1}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setMostrarSecret(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fas ${mostrarSecret ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <small className="text-warning-emphasis">
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      Alterar aqui derruba a validação até atualizar o mesmo segredo na Convenia.
                    </small>
                  </div>

                  {formulario.id && (
                    <div className="col-12">
                      <div className="border-top pt-3 d-flex flex-wrap gap-4 text-muted"
                        style={{ fontSize: '.84rem' }}>
                        <span>
                          <i className="fas fa-hashtag me-1"></i>
                          ID <b className="text-dark">{formulario.id}</b>
                        </span>
                        <span>
                          <i className="fas fa-calendar-plus me-1"></i>
                          Criado em <b className="text-dark">{formulario.created_at || '-'}</b>
                        </span>
                        <span>
                          <i className="fas fa-clock-rotate-left me-1"></i>
                          Atualizado em <b className="text-dark">{formulario.updated_at || '-'}</b>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={fecharModal} disabled={salvando}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success" onClick={salvarCliente} disabled={salvando}>
                  {salvando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      {formulario.id ? 'Salvar Alterações' : 'Cadastrar'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE SERVIÇOS DO CLIENTE */}
      {clienteServicos && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1140px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-layer-group me-2"></i>
                    Serviços — {clienteServicos.cliente}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {carregandoServicos
                      ? 'Carregando...'
                      : `${servicos.length} serviço(s) em ${servicosPorCategoria.length} categoria(s)`}
                    {clienteServicos.operador && ` · operador: ${clienteServicos.operador}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setClienteServicos(null)}></button>
              </div>

              <div className="modal-body" ref={corpoServicosRef}
                style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Cadastro/edição de serviço */}
                {servicoForm ? (
                  <div className="border rounded p-3 bg-light mb-4"
                    style={{ borderLeft: '4px solid #00b5cc' }}>
                    <div className="fw-bold mb-3" style={{ color: '#212529' }}>
                      <i className={`fas ${servicoForm.id ? 'fa-edit' : 'fa-plus'} me-2 text-primary`}></i>
                      {servicoForm.id
                        ? `Editando: ${servicoForm.propriedade || 'serviço'}`
                        : 'Novo serviço'}
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">
                          Categoria <span className="text-danger">*</span>
                        </label>
                        <input type="text" className="form-control form-control-sm" list="cat-servico"
                          placeholder="Ex: Ponto" value={servicoForm.categoria}
                          onChange={setServ('categoria')} disabled={salvandoServico} />
                        <datalist id="cat-servico">
                          {categoriasDisponiveis.map(c => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">
                          Propriedade (sistema) <span className="text-danger">*</span>
                        </label>
                        <input type="text" className="form-control form-control-sm"
                          placeholder="Ex: Pontomais" value={servicoForm.propriedade}
                          onChange={setServ('propriedade')} disabled={salvandoServico} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Portal (URL)</label>
                        <input type="text" className="form-control form-control-sm"
                          placeholder="https://..." value={servicoForm.portal}
                          onChange={setServ('portal')} disabled={salvandoServico} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Login</label>
                        <input type="text" className="form-control form-control-sm"
                          placeholder="usuario@empresa.com.br" value={servicoForm.login}
                          onChange={setServ('login')} disabled={salvandoServico}
                          autoComplete="off" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Senha</label>
                        <div className="input-group input-group-sm">
                          <input type={mostrarSenhaForm ? 'text' : 'password'}
                            className="form-control form-control-sm"
                            value={servicoForm.senha} onChange={setServ('senha')}
                            placeholder={servicoForm.tem_senha ? 'em branco = mantém a atual' : ''}
                            disabled={salvandoServico} autoComplete="new-password" />
                          {servicoForm.tem_senha && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              onClick={() => revelarNoServico('senha')} tabIndex={-1}
                              title="Carregar a senha atual para editar">
                              <i className="fas fa-download"></i>
                            </button>
                          )}
                          <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => setMostrarSenhaForm(v => !v)} tabIndex={-1}>
                            <i className={`fas ${mostrarSenhaForm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">E-mail de reset</label>
                        <input type="text" className="form-control form-control-sm"
                          placeholder="para recuperar a senha" value={servicoForm.email_reset}
                          onChange={setServ('email_reset')} disabled={salvandoServico} />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label small fw-bold mb-1">API Token</label>
                        <div className="input-group input-group-sm">
                          <input type="text" className="form-control form-control-sm"
                            placeholder={servicoForm.tem_api_token ? 'em branco = mantém o atual' : 'opcional'}
                            value={servicoForm.api_token}
                            onChange={setServ('api_token')} disabled={salvandoServico}
                            autoComplete="off" />
                          {servicoForm.tem_api_token && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              onClick={() => revelarNoServico('api_token')} tabIndex={-1}
                              title="Carregar o token atual para editar">
                              <i className="fas fa-download"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4 d-flex align-items-end gap-2">
                        <button className="btn btn-sm btn-success flex-fill" onClick={salvarServico}
                          disabled={salvandoServico}>
                          {salvandoServico
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <><i className="fas fa-save me-2"></i>{servicoForm.id ? 'Salvar' : 'Adicionar'}</>}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setServicoForm(null)}
                          disabled={salvandoServico}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button className="btn btn-sm btn-primary" onClick={abrirNovoServico}>
                      <i className="fas fa-plus me-2"></i>
                      Novo serviço
                    </button>
                  </div>
                )}

                {/* Confirmação de exclusão de serviço */}
                {servicoExcluir && (
                  <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Excluir o serviço <strong>{servicoExcluir.propriedade}</strong>
                      {servicoExcluir.categoria && ` (${servicoExcluir.categoria})`}? As credenciais
                      guardadas nele serão perdidas.
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => setServicoExcluir(null)}
                        disabled={salvandoServico}>
                        Cancelar
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={confirmarExclusaoServico}
                        disabled={salvandoServico}>
                        {salvandoServico
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="fas fa-trash me-2"></i>Excluir</>}
                      </button>
                    </div>
                  </div>
                )}

                {carregandoServicos ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : servicos.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Este cliente ainda não tem serviços cadastrados. Use
                    <strong> Novo serviço</strong> acima para adicionar o primeiro.
                  </div>
                ) : (
                  servicosPorCategoria.map(([categoria, itens]) => (
                    <div key={categoria} className="mb-4">
                      <h6 className="fw-bold mb-2" style={{ color: '#212529' }}>
                        {categoria}
                        <span className="badge bg-light text-dark border ms-2">{itens.length}</span>
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-striped align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Propriedade</th>
                              <th>Portal</th>
                              <th>Login</th>
                              <th>Senha</th>
                              <th>E-mail de reset</th>
                              <th>API Token</th>
                              <th className="text-center">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itens.map((s) => (
                              <tr key={s.id}
                                style={servicoForm?.id === s.id || servicoExcluir?.id === s.id
                                  ? { boxShadow: 'inset 3px 0 0 #00b5cc', background: '#eefbfd' }
                                  : undefined}>
                                <td className="fw-bold">{s.propriedade}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {/* URLs de portal chegam com query string enorme (OAuth) e
                                      estouravam a coluna — mostra só o rótulo, URL no title */}
                                  {s.portal ? (
                                    <a
                                      href={/^https?:\/\//i.test(s.portal) ? s.portal : `https://${s.portal}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={s.portal}
                                      style={{ fontSize: '.84rem' }}
                                    >
                                      <i className="fas fa-link me-1"></i>
                                      Link portal
                                    </a>
                                  ) : <span className="text-muted">-</span>}
                                </td>
                                <td style={{ wordBreak: 'break-all', fontSize: '.84rem' }}>
                                  {s.login ? (
                                    <span className="d-inline-flex align-items-center gap-1">
                                      {s.login}
                                      <button className="btn btn-sm btn-light p-1"
                                        onClick={() => copiarTexto(s.login, 'Login')} title="Copiar login">
                                        <i className="fas fa-copy text-muted"></i>
                                      </button>
                                    </span>
                                  ) : <span className="text-muted">-</span>}
                                </td>
                                <td>{mascarar(s.tem_senha, `sv-senha-${s.id}`, 'servico', s.id, 'senha')}</td>
                                <td style={{ wordBreak: 'break-all', fontSize: '.84rem' }}>
                                  {s.email_reset || <span className="text-muted">-</span>}
                                </td>
                                <td>{mascarar(s.tem_api_token, `sv-token-${s.id}`, 'servico', s.id, 'api_token')}</td>
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                  <button className="btn btn-sm btn-outline-primary me-1"
                                    onClick={() => abrirEdicaoServico(s)} title="Editar serviço">
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-danger"
                                    onClick={() => pedirExclusaoServico(s)} title="Excluir serviço">
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="modal-footer bg-light">
                <small className="text-muted me-auto">
                  <i className="fas fa-lock me-1"></i>
                  Credenciais de acesso — não compartilhar fora do time responsável.
                </small>
                <button type="button" className="btn btn-secondary" onClick={() => setClienteServicos(null)}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      {/* MODAL — SAIR / VOLTAR PARA A CARTEIRA DO BPO RH */}
      {clienteSituacao && (
        <div
          className="modal"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,.5)', zIndex: 10000 }}
          onClick={() => !salvandoSituacao && setClienteSituacao(null)}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '620px' }}>
            <div className="modal-content shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    {clienteSituacao.ativo === false
                      ? 'Trazer de volta para o BPO RH'
                      : 'Tirar da carteira do BPO RH'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>{clienteSituacao.cliente}</small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  disabled={salvandoSituacao}
                  onClick={() => setClienteSituacao(null)}
                ></button>
              </div>

              <div className="modal-body">
                {clienteSituacao.ativo === false ? (
                  <>
                    <p className="mb-2">
                      O cliente volta a aparecer em todos os módulos do BPO RH e no painel do
                      operador, com o histórico que já existia.
                    </p>
                    {previaSituacao?.credencial_guardada && (
                      <div className="alert alert-info py-2 mb-0">
                        <i className="fas fa-plug me-2"></i>
                        A credencial da Convenia guardada na saída volta a valer, e o sync
                        recomeça a atualizar o espelho deste cliente.
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mb-2">
                      <strong>Nada é apagado.</strong> O cliente sai das listas de todos os
                      módulos e do painel do operador — com ele somem as tarefas em atraso dele.
                      O histórico continua no banco para auditoria e para os meses já fechados.
                    </p>

                    {previaSituacao === null ? (
                      <div className="text-muted small mb-3">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        conferindo o que ainda está em aberto...
                      </div>
                    ) : previaSituacao.erro ? (
                      <div className="alert alert-warning py-2">
                        Não consegui conferir o que está em aberto.
                      </div>
                    ) : (
                      <ul className="list-group list-group-flush mb-3">
                        <li className="list-group-item d-flex justify-content-between px-0">
                          <span>Admissões em aberto</span>
                          <strong>{previaSituacao.abertos?.admissoes ?? 0}</strong>
                        </li>
                        <li className="list-group-item d-flex justify-content-between px-0">
                          <span>Rescisões em aberto</span>
                          <strong>{previaSituacao.abertos?.rescisoes ?? 0}</strong>
                        </li>
                        <li className="list-group-item d-flex justify-content-between px-0">
                          <span>Colaboradores ativos no espelho</span>
                          <strong>{previaSituacao.abertos?.colaboradores_ativos ?? 0}</strong>
                        </li>
                      </ul>
                    )}

                    {previaSituacao?.sync_ligado && (
                      <div className="alert alert-warning py-2">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        A credencial da Convenia deste cliente será guardada e retirada de uso,
                        para o sync parar de repovoar o espelho. Ela volta se o cliente voltar.
                      </div>
                    )}

                    <label className="form-label fw-bold mb-1">
                      Motivo da saída <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={255}
                      placeholder="Ex.: encerrou o contrato de BPO de RH em 09/2026"
                      value={motivoSaida}
                      onChange={(e) => setMotivoSaida(e.target.value)}
                    />
                    <div className="form-text">Fica registrado junto com a data da saída.</div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button
                  className="btn btn-secondary"
                  disabled={salvandoSituacao}
                  onClick={() => setClienteSituacao(null)}
                >
                  Cancelar
                </button>
                <button
                  className={`btn ${clienteSituacao.ativo === false ? 'btn-success' : 'btn-dark'}`}
                  disabled={salvandoSituacao
                    || (clienteSituacao.ativo !== false && !motivoSaida.trim())}
                  onClick={confirmarSituacao}
                >
                  {salvandoSituacao && <span className="spinner-border spinner-border-sm me-2"></span>}
                  {clienteSituacao.ativo === false ? 'Trazer de volta' : 'Tirar da carteira'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {clienteExcluir && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                  Confirmar Exclusão
                </h5>
                <button type="button" className="btn-close" onClick={() => setClienteExcluir(null)} disabled={excluindo}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">Tem certeza que deseja excluir este cliente?</p>
                <div className="alert alert-warning mb-0">
                  <strong>{clienteExcluir.cliente}</strong>
                  {clienteExcluir.total_servicos > 0 && (
                    <div className="small mt-1">
                      <i className="fas fa-info-circle me-1"></i>
                      Os {clienteExcluir.total_servicos} serviço(s) deste cliente ficarão sem cliente vinculado
                      (não serão excluídos).
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setClienteExcluir(null)} disabled={excluindo}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmarExclusao} disabled={excluindo}>
                  {excluindo ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Excluindo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Excluir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhClientesCadastro;
